import axiosConfig from "./config/axiosConfig";
import { REFRESH_TOKEN_TYPE } from "./constant/asynActionsType";
import { refreshToken } from "./redux/features/auth/authActions";
import { store } from "./redux/store";
import {
  getAccessToken,
  getRefreshToken,
  logout,
  setAccessToken,
} from "./utils/tokenStorage";

// Track refresh token state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor
axiosConfig.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosConfig.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosConfig(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshTokenValue = getRefreshToken();

      if (!refreshTokenValue) {
        logout();
        return Promise.reject(error);
      }

      try {
        const response = await store.dispatch(
          refreshToken({ refresh: refreshTokenValue })
        );

        if (response.type === `${REFRESH_TOKEN_TYPE}/fulfilled`) {
          const newToken = response.payload.token;
          setAccessToken(newToken);

          // Update the failed request's authorization header
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Process all queued requests
          processQueue(null, newToken);

          return axiosConfig(originalRequest);
        } else {
          processQueue(new Error("Token refresh failed"), null);
          logout();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
